import { useAppSelector } from "@/hooks/storeHooks.ts";
import { IMatter, UserObject } from "@/types/SPTypes.tsx";
import {
  getMattersForCurrentUser,
  getMattersForCurrentUserWithComments
} from "@/utils/sharepointAPI.ts";
import { useEffect, useState } from "react";
import { Col, ListGroup, Row } from "react-bootstrap";
import "./MyMatters.css";
import { FrameColor, MatterStatus, MatterStatusSv } from "@/utils/enums.ts";
import SelectedMatterOverview from "./SelectedMatterOverview.tsx";
import { formatDateTime, truncateText } from "@/utils/utils.ts";
import { FrameComponent } from "fmv-component-ui-library";

function MyMatters() {
  const PAGE_SIZE = 5;
  const currentUser =
    useAppSelector(state => state.application.currentUser) || UserObject;

  const [userMatters, setUserMatters] = useState<IMatter[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedMatter, setSelectedMatter] = useState<IMatter>();
  const [comments, setComments] = useState<any[]>();
  const openMatters = userMatters.filter(
    (matter: IMatter) =>
      matter.status !== MatterStatus.ARCHIVED &&
      matter.status !== MatterStatus.DRAFT
  );
  const totalCount = openMatters?.length ?? 0;
  const visibleMatters = (openMatters ?? []).slice(0, visibleCount);
  const hasMoreToShow = totalCount > visibleCount;
  const canShowLess = visibleCount > PAGE_SIZE;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [openMatters?.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const matters = await getMattersForCurrentUser(currentUser);
        setUserMatters(matters);
      } catch (error) {
        console.error("Failed to fetch matters: ", error);
      }
    };
    fetchData();
  }, []);

  const handleShowMore = () => {
    setVisibleCount(current => Math.min(current + PAGE_SIZE, totalCount));
  };

  const handleShowLess = () => {
    setVisibleCount(PAGE_SIZE);
  };

  function getStatusLabel(status: MatterStatus): string {
    return MatterStatusSv[status] ?? status;
  }
  const getComments = async (ID: number) => {
    const res = await getMattersForCurrentUserWithComments(ID);
    setComments(res);
  };

  const sortedMatters = [...visibleMatters].sort((a, b) => {
    if (
      getStatusLabel(a.status as MatterStatus) === MatterStatus.CLOSED &&
      getStatusLabel(b.status as MatterStatus) !== MatterStatus.CLOSED
    )
      return 1;
    if (
      getStatusLabel(a.status as MatterStatus) !== MatterStatus.CLOSED &&
      getStatusLabel(b.status as MatterStatus) === MatterStatus.CLOSED
    )
      return -1;
    return 0;
  });

  return (
    <FrameComponent
      color={FrameColor.BLUE}
      headerAlign="left"
      header="Mina ärenden"
    >
      <div className="page-container">
        <h2>
          Detta är en överblick av dina ärenden skickade till VSDL senaste 2
          åren. Här kan du se både pågående och stängda ärenden, följa deras
          status, se uppdateringar, information om ärendet samt när ärendesvar
          har getts.
        </h2>
        <Row>
          <Col xs={12} md={6}>
            <div className="open-matter-overview-container">
              <ListGroup>
                {sortedMatters.length ? (
                  sortedMatters.map(matter => (
                    <ListGroup.Item
                      className="glass-item mb-2"
                      key={matter.ID}
                      onClick={() => {
                        setSelectedMatter(matter);
                        getComments(matter.ID);
                      }}
                    >
                      <div className="matter-list-info">
                        <div>
                          <strong>
                            {truncateText(matter.matterTitle, 20)}
                          </strong>
                          <p>
                            Senast hanterad:{" "}
                            {formatDateTime(
                              matter.modifiedDate,
                              matter.Modified
                            )}
                          </p>
                        </div>
                        <div className="matter-list-status-container">
                          <strong>Status</strong>
                          <p>{getStatusLabel(matter.status as MatterStatus)}</p>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))
                ) : (
                  <ListGroup.Item disabled>
                    Inga ärenden hittades...
                  </ListGroup.Item>
                )}
              </ListGroup>

              {(hasMoreToShow || canShowLess) && (
                <div className="show-matters-container">
                  {hasMoreToShow && (
                    <a onClick={handleShowMore} role="button">
                      Visa fler ({totalCount - visibleCount} kvar)
                    </a>
                  )}
                  {canShowLess && (
                    <a role="button" onClick={handleShowLess}>
                      Minimera
                    </a>
                  )}
                </div>
              )}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <ListGroup>
              {selectedMatter ? (
                <SelectedMatterOverview
                  matter={selectedMatter}
                  comments={comments}
                />
              ) : (
                <p>Välj ärende för att läsa mer...</p>
              )}
            </ListGroup>
          </Col>
        </Row>
      </div>
    </FrameComponent>
  );
}

export default MyMatters;
